// MorrowOS — Connect your browser.
//
// Pairs the browser-loop extension so the agent can act in the user's real
// browser. Flow: generate a pairing token here → install/open the MorrowOS
// Browser extension → paste this token + the API base into its popup. A live
// status dot shows when the browser is online (it long-polls the backend).
//
// Backed by /api/morrow/browser/{pair,status}.

import { ArrowLeftIcon, CheckIcon, CopyIcon, GlobeIcon, Loader2Icon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { apiUrl } from "../../apiBase";
import { resolveComposioUserId } from "../../composioConnect";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { Button } from "../ui/button";
import { BrowserConnectionsList } from "./BrowserConnectionsList";
import { SettingsPageContainer } from "./settingsLayout";

const API_BASE = "https://api.morrowos.com";

export function BrowserConnectPage() {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, userId: clerkUserId } = useMorrowClerkAuth();
  const userId = useMemo(
    () => resolveComposioUserId(isLoaded && isSignedIn ? clerkUserId : null),
    [isLoaded, isSignedIn, clerkUserId],
  );

  const [token, setToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [online, setOnline] = useState(false);
  const [copied, setCopied] = useState(false);

  // Live online status — the extension marks itself online by long-polling.
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    const check = () => {
      void fetch(apiUrl(`/api/morrow/browser/status?userId=${encodeURIComponent(userId)}`), {
        headers: { accept: "application/json" },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d: { online?: boolean } | null) => {
          if (alive && d) setOnline(Boolean(d.online));
        })
        .catch(() => {});
    };
    check();
    const t = setInterval(check, 5000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [userId]);

  const generate = useCallback(async () => {
    if (!userId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(apiUrl("/api/morrow/browser/pair"), {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({ userId }),
      });
      const data = (await res.json().catch(() => null)) as {
        token?: string;
        error?: string;
      } | null;
      if (!res.ok || !data?.token) {
        setError(data?.error ?? "Couldn't generate a pairing token.");
      } else {
        setToken(data.token);
      }
    } catch {
      setError("Couldn't reach the server.");
    } finally {
      setBusy(false);
    }
  }, [userId]);

  const copy = useCallback((text: string) => {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, []);

  return (
    <SettingsPageContainer>
      <div className="mx-auto flex w-full max-w-xl flex-col gap-4 px-4 py-6">
        <button
          type="button"
          onClick={() => void navigate({ to: "/settings/connections" })}
          className="flex items-center gap-1.5 self-start text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="size-3.5" /> Connections
        </button>

        <div className="flex flex-col items-center gap-2 text-center">
          <span className="flex size-12 items-center justify-center rounded-xl border border-border bg-muted">
            <GlobeIcon className="size-6 text-foreground" />
          </span>
          <h1 className="text-lg font-semibold text-foreground">Connect your browser</h1>
          <p className="max-w-md text-sm text-muted-foreground">
            Let Morrow act in your real browser — open pages, read them, and do tasks in your own
            logged-in sessions. Install the MorrowOS Browser extension, then pair it below.
          </p>
          <span
            className={
              online
                ? "inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-500"
                : "inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground"
            }
          >
            <span
              className={`size-2 rounded-full ${online ? "bg-emerald-500" : "bg-muted-foreground/40"}`}
            />
            {online ? "Browser connected" : "No browser connected yet"}
          </span>
        </div>

        <BrowserConnectionsList userId={userId} />

        <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5">
          <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm text-muted-foreground">
            <li>Install the MorrowOS Browser extension (Chrome/Edge/Firefox).</li>
            <li>Generate a pairing token here and copy it.</li>
            <li>Open the extension, paste the API URL and token, and hit Pair.</li>
          </ol>

          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-medium text-muted-foreground">API URL</span>
            <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
              <code className="flex-1 truncate text-xs text-foreground">{API_BASE}</code>
              <button
                type="button"
                onClick={() => copy(API_BASE)}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Copy API URL"
              >
                <CopyIcon className="size-3.5" />
              </button>
            </div>
          </div>

          {token ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-medium text-muted-foreground">
                Pairing token (paste into the extension)
              </span>
              <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
                <code className="flex-1 truncate text-xs text-foreground">{token}</code>
                <button
                  type="button"
                  onClick={() => copy(token)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Copy token"
                >
                  {copied ? (
                    <CheckIcon className="size-3.5 text-emerald-500" />
                  ) : (
                    <CopyIcon className="size-3.5" />
                  )}
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Keep this private — anyone with it can drive your browser. Generate a new one to
                revoke access to the old.
              </p>
            </div>
          ) : null}

          {error ? <p className="text-xs text-destructive">{error}</p> : null}

          <Button onClick={() => void generate()} disabled={busy || !userId} className="w-full">
            {busy ? (
              <>
                <Loader2Icon className="size-4 animate-spin" /> Generating…
              </>
            ) : token ? (
              "Generate a new token"
            ) : (
              "Generate pairing token"
            )}
          </Button>
        </div>
      </div>
    </SettingsPageContainer>
  );
}
